import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, Lock, Mail, ArrowRight } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("admin@up2finance.com");
  const [password, setPassword] = useState("Up2Admin@2026");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo ao UP2 Finance Hub");
      nav("/");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Falha no login";
      toast.error(
        typeof detail === "string" ? detail : "Credenciais inválidas",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex bg-zinc-950 text-white p-10 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-30 grid-bg" />
        <div className="relative z-10">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img
              src="icone-logo-up2.png"
              alt="UP2 Soluções"
              className="h-20 w-20 object-contain"
            />
            <div>
              <div className="font-display font-bold text-lg leading-none tracking-tight">
                UP2 FINANCE HUB
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mt-1 font-mono">
                HUB de Integração Bancária
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
            Um HUB.
            <br />
            Seis bancos.
            <br />
            <span className="text-[#F26522]">Diversas empresas.</span>
          </h1>
          <p className="text-zinc-400 leading-relaxed">
            Plataforma SaaS multi-tenant para emissão, conciliação e cobrança de
            boletos, boletos híbridos e PIX. Banco do Brasil, Inter, Itaú,
            Bradesco, Santander e Sicredi em uma única API.
          </p>
          <div className="flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
            <span className="px-2 py-1 border border-zinc-800">
              Multi-tenant
            </span>
            <span className="px-2 py-1 border border-zinc-800">Webhooks</span>
            <span className="px-2 py-1 border border-zinc-800">PIX QR</span>
            <span className="px-2 py-1 border border-zinc-800">Integração MV</span>
          </div>
        </div>
        <div className="relative z-10 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          v1.0.0
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <Card className="w-full max-w-md p-8 rounded-sm border border-zinc-200 shadow-none">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img
              src="icone-logo-up2.png"
              alt="UP2 Soluções"
              className="h-20 w-20 object-contain"
            />
            <span className="font-display font-bold">UP2 FINANCE HUB</span>
          </div>
          <div className="mb-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Acesso restrito
            </div>
            <h2 className="font-display text-3xl font-bold">Entrar</h2>
            <p className="text-sm text-zinc-500 mt-2">
              Use suas credenciais corporativas para acessar o painel.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label
                htmlFor="email"
                className="text-xs uppercase tracking-wider font-semibold"
              >
                E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 rounded-sm h-11"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className="text-xs uppercase tracking-wider font-semibold"
              >
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 rounded-sm h-11"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-sm font-semibold tracking-wide"
              data-testid="login-submit-button"
            >
              {busy ? (
                "Entrando..."
              ) : (
                <>
                  Entrar <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200 text-sm text-zinc-500">
            Ainda não tem conta?{" "}
            <Link
              to="/register"
              className="text-zinc-900 font-semibold hover:underline"
              data-testid="link-register"
            >
              Cadastrar empresa →
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
